import { useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { CheckIcon, ChevronsUpDownIcon, LayersIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { IdentityTexture } from '@/shared/components/IdentityTexture';
import { accentFor } from '@/shared/components/identityAccent';
import { useWorkspaceList, workspaceInitial } from '@/features/workspaces';
import { WorkspaceMuteItem } from '@/features/notifications';
import { workspaceSwitchTarget } from '../workspaceSwitch';

export interface WorkspaceIdentityProps {
  workspaceId: string;
  /** Absent until the workspace read resolves. */
  name?: string;
  workflowCount?: number;
  taskCount?: number;
  collapsed?: boolean;
  className?: string;
}

/**
 * The workspace switcher.
 *
 * The card shows the current workspace; opening it lists every workspace the
 * service knows. The list query mounts disabled and fetches on first open, so
 * the shell still performs exactly one read for its own workspace.
 */
export function WorkspaceIdentity({
  workspaceId,
  name,
  workflowCount,
  taskCount,
  collapsed = false,
  className,
}: WorkspaceIdentityProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const workspaceList = useWorkspaceList({ enabled: open });

  const summary =
    typeof workflowCount === 'number' && typeof taskCount === 'number'
      ? `${String(workflowCount)} workflows · ${String(taskCount)} tasks`
      : workspaceId;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        aria-label={`Current workspace: ${name ?? workspaceId}. Switch workspace`}
        className={cn(
          'group border-sidebar-border bg-card flex w-full items-center gap-2.5 rounded-lg border p-2 text-left',
          'hover:bg-sidebar-accent transition-colors',
          'focus-visible:ring-sidebar-ring focus-visible:ring-3 focus-visible:outline-none',
          collapsed && 'justify-center border-0 bg-transparent p-0',
          className,
        )}
      >
        {/* The other end of the workspace-list monogram's transition.
            `accentFor`, not `bg-primary`: this used to be blue while the list
            drew the same workspace in one of the eight slots, so the transition
            FR-026 asks to read as continuity morphed a pastel square into a blue
            one. Same accent and same texture at both ends is what makes it one
            object seen twice rather than two objects crossfading. */}
        <span
          aria-hidden="true"
          style={{ viewTransitionName: `workspace-${workspaceId}` }}
          className={cn(
            'relative flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md text-sm font-semibold',
            accentFor(workspaceId),
          )}
        >
          <IdentityTexture id={workspaceId} />
          <span className="relative">{workspaceInitial(name)}</span>
        </span>
        {collapsed ? null : (
          <>
            <span className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="text-sidebar-foreground truncate text-sm font-medium">
                {name ?? 'Workspace'}
              </span>
              <span className="text-muted-foreground truncate text-xs">
                {summary}
              </span>
            </span>
            <ChevronsUpDownIcon
              aria-hidden="true"
              className="text-muted-foreground size-4 shrink-0"
              strokeWidth={1.5}
            />
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
          {workspaceList.isPending ? (
            <DropdownMenuItem disabled>Loading workspaces…</DropdownMenuItem>
          ) : null}
          {workspaceList.isError ? (
            <DropdownMenuItem disabled>
              The workspaces could not be loaded.
            </DropdownMenuItem>
          ) : null}
          {workspaceList.data?.items.map((workspace) => (
            <DropdownMenuItem
              key={workspace.id}
              onClick={() => {
                if (workspace.id === workspaceId) return;
                void navigate(workspaceSwitchTarget(pathname, workspace.id));
              }}
            >
              <span className="flex-1 truncate">{workspace.name}</span>
              {workspace.id === workspaceId ? (
                <>
                  <CheckIcon
                    aria-hidden="true"
                    className="size-4 shrink-0"
                    strokeWidth={1.5}
                  />
                  <span className="sr-only">(current)</span>
                </>
              ) : null}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        {/* Muting lives with the workspace it mutes (spec 142 US4). */}
        <WorkspaceMuteItem
          workspaceId={workspaceId}
          name={name ?? 'this workspace'}
          open={open}
        />
        <DropdownMenuItem
          onClick={() => {
            void navigate({ to: '/' });
          }}
        >
          <LayersIcon
            aria-hidden="true"
            className="size-4 shrink-0"
            strokeWidth={1.5}
          />
          All workspaces
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
